import React, { FunctionComponent } from 'react'

import styled, { useTheme } from 'styled-components/native'

import { ActivityBadge } from '../../atoms/activityBadge/ActivityBadge'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { CircleIcon } from '../../atoms/icons/CircleIcon'
import { Title } from '../../atoms/title/Title'
import { BadgeShape, BadgeVariant } from '../../badge/constants'
import { HoverActionContainer } from '../../organisms/hoverActionContainer/HoverActionContainer'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'
import { HoverActionItem } from '../hoverActions/HoverActions'

export type ClientInvitationRowProps = {
  id: string
  clientName: string
  details: string
  badgeLabel: string
  badgeVariant: BadgeVariant
  hoverActions: HoverActionItem[]
  showNotificationDot?: boolean
}

const Container = styled(HoverActionContainer)(({ theme }) => ({
  paddingHorizontal: theme.spacing['16px'],
  borderBottomWidth: 1,
  borderBottomColor: theme.colors.borderDefault,
}))

const ContentRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  paddingVertical: theme.spacing['12px'],
  gap: theme.spacing['8px'],
}))

const ClientInfoSection = styled.View(({ theme }) => ({
  flexBasis: '65%',
  flexShrink: 1,
  flexGrow: 0,
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const NotificationDotContainer = styled.View({
  width: 10,
  alignItems: 'center',
  justifyContent: 'center',
})

const ClientInfoStack = styled.View({
  flexShrink: 1,
  flexDirection: 'column',
})

export const ClientInvitationRow: FunctionComponent<ClientInvitationRowProps> = ({
  id,
  clientName,
  details,
  badgeLabel,
  badgeVariant,
  hoverActions,
  showNotificationDot = false,
}) => {
  const { colors } = useTheme()

  return (
    <Container items={hoverActions} testID={tID(`ClientInvitationRow-${id}`)}>
      <ContentRow>
        <ClientInfoSection>
          <NotificationDotContainer>
            {showNotificationDot && <CircleIcon size={10} isFilled fillColor={colors.iconActive} />}
          </NotificationDotContainer>
          <ClientInfoStack>
            <Title text={clientName} size={TitleSize.XSMALL} />
            <BodyText text={details} size={BodyTextSize.SMALL} color={colors.textSecondary} />
          </ClientInfoStack>
        </ClientInfoSection>
        <ActivityBadge
          text={badgeLabel}
          variant={badgeVariant}
          shape={BadgeShape.RECT}
          bold
          testID={tID(`ClientInvitationRow-badge-${id}`)}
        />
      </ContentRow>
    </Container>
  )
}
